import { PrivacyBlur } from "#components/privacy-blur"
import { useCurrencyFormatters } from "#hooks/use-currency-formatters"
import { useIsMobile } from "#hooks/use-mobile"
import type { RechartsSankeyNode } from "@home-finance-manager/shared/types/dashboard"
import { motion } from "framer-motion"
import { useEffect, useRef, useState } from "react"

export interface SankeyTooltipData {
  sourceNode: RechartsSankeyNode
  targetNode: RechartsSankeyNode
  value: number
  partnerPercentage?: number
  categoryPercentage?: number
  x: number
  y: number
}

interface UseSankeyTooltipProps {
  nodes: RechartsSankeyNode[]
}

export const useSankeyTooltip = ({ nodes }: UseSankeyTooltipProps) => {
  const isMobile = useIsMobile()
  const { formatCurrency } = useCurrencyFormatters()
  const [tooltipData, setTooltipData] = useState<SankeyTooltipData | null>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const [tooltipPosition, setTooltipPosition] = useState<{
    left: string
    top: string
    transform: string
  } | null>(null)
  const pendingTooltipDataRef = useRef<SankeyTooltipData | null>(null)
  const mousePositionRef = useRef<{ x: number; y: number } | null>(null)

  // Track mouse/touch position for tooltip positioning
  useEffect(() => {
    if (!isMobile) return

    const handleMouseMove = (e: MouseEvent) => {
      const pos = { x: e.clientX, y: e.clientY }
      mousePositionRef.current = pos
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) {
        const touch = e.touches[0]
        const pos = { x: touch.clientX, y: touch.clientY }
        mousePositionRef.current = pos
      }
    }

    window.addEventListener("mousemove", handleMouseMove)
    window.addEventListener("touchmove", handleTouchMove, { passive: true })

    return () => {
      window.removeEventListener("mousemove", handleMouseMove)
      window.removeEventListener("touchmove", handleTouchMove)
    }
  }, [isMobile])

  // Update tooltip data from ref (deferred from render)
  useEffect(() => {
    if (pendingTooltipDataRef.current) {
      setTooltipData(pendingTooltipDataRef.current)
      pendingTooltipDataRef.current = null
    }
  })

  // Calculate tooltip position to keep it within viewport (simplified using mouse position)
  useEffect(() => {
    if (!isMobile || !tooltipData) {
      setTooltipPosition(null)
      return
    }

    // Use stored coordinates (captured at tooltip trigger time) - tooltip should stay at trigger point vertically
    const pointerY = tooltipData.y

    let retryCount = 0
    const maxRetries = 10

    // Wait for tooltip to render before measuring
    const calculatePosition = () => {
      if (!tooltipRef.current) {
        if (retryCount < maxRetries) {
          retryCount++
          requestAnimationFrame(calculatePosition)
        } else {
          // Fallback to default position if tooltip never renders (centered horizontally)
          const viewportWidth = window.innerWidth
          setTooltipPosition({
            left: `${viewportWidth / 2}px`,
            top: `${pointerY - 10}px`,
            transform: "translate(-50%, -100%)",
          })
        }
        return
      }

      const tooltip = tooltipRef.current
      const tooltipRect = tooltip.getBoundingClientRect()

      // If tooltip hasn't rendered yet (dimensions are 0), try again
      if ((tooltipRect.width === 0 || tooltipRect.height === 0) && retryCount < maxRetries) {
        retryCount++
        requestAnimationFrame(calculatePosition)
        return
      }

      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      const padding = 8 // Padding from viewport edges

      // Use actual dimensions if available, otherwise estimate
      const tooltipHeight = tooltipRect.height || 100

      // Always center horizontally on the screen
      const left = viewportWidth / 2
      const transformX = "-50%"

      // Calculate vertical position
      let top = pointerY - 10
      let transformY = "-100%" // Default: position above the touch point

      // Check if tooltip would overflow on the top
      if (pointerY - tooltipHeight - 10 < padding) {
        // Position below instead
        top = pointerY + 10
        transformY = "0%"
      }
      // Check if tooltip would overflow on the bottom
      else if (pointerY + 10 + tooltipHeight > viewportHeight - padding) {
        // Try to position above, but adjust if needed
        top = Math.max(padding, pointerY - tooltipHeight - 10)
        transformY = top === padding ? "0%" : "-100%"
      }

      setTooltipPosition({
        left: `${left}px`,
        top: `${top}px`,
        transform: `translate(${transformX}, ${transformY})`,
      })
    }

    // Use requestAnimationFrame to ensure DOM has updated
    requestAnimationFrame(calculatePosition)
  }, [isMobile, tooltipData])

  const closeTooltip = () => {
    setTooltipData(null)
  }

  // Custom tooltip component for Recharts
  const CustomTooltip = ({ active, payload, coordinate }: any) => {
    // On mobile, show persistent tooltip if tooltipData is set
    if (isMobile && tooltipData) {
      // Use calculated position if available, otherwise use default (centered horizontally)
      const position = tooltipPosition || {
        left: `${window.innerWidth / 2}px`,
        top: `${tooltipData.y - 10}px`,
        transform: "translate(-50%, -100%)",
      }

      return (
        <motion.div
          ref={tooltipRef}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.15 }}
          className="fixed rounded-md border bg-popover p-3 text-sm shadow-md z-50 pointer-events-auto"
          style={{
            ...position,
            maxWidth: "calc(100vw - 16px)",
            minWidth: "250px",
          }}
        >
          <div className="flex items-center gap-2 font-medium flex-wrap">
            {/* Source color bar - ensure it's visible */}
            <div
              className="flex-shrink-0"
              style={{
                width: "4px",
                height: "20px",
                backgroundColor: tooltipData.sourceNode.color || "#3b82f6",
                borderRadius: "2px",
                display: "inline-block",
                flexShrink: 0,
                verticalAlign: "middle",
                minWidth: "4px",
                minHeight: "20px",
              }}
              title={`Source: ${tooltipData.sourceNode.name} (${tooltipData.sourceNode.color || "#3b82f6"})`}
            />
            <span className="truncate">{tooltipData.sourceNode.name}</span>
            <span className="text-muted-foreground">→</span>
            <span className="truncate">{tooltipData.targetNode.name}</span>
            {/* Target color bar - ensure it's visible */}
            <div
              className="flex-shrink-0"
              style={{
                width: "4px",
                height: "20px",
                backgroundColor: tooltipData.targetNode.color || "#94a3b8",
                borderRadius: "2px",
                display: "inline-block",
                flexShrink: 0,
                verticalAlign: "middle",
                minWidth: "4px",
                minHeight: "20px",
              }}
              title={`Target: ${tooltipData.targetNode.name} (${tooltipData.targetNode.color || "#94a3b8"})`}
            />
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            <PrivacyBlur>
              {(() => {
                const percentageParts: string[] = []
                if (
                  tooltipData.partnerPercentage !== undefined &&
                  tooltipData.partnerPercentage > 0
                ) {
                  percentageParts.push(`${tooltipData.partnerPercentage.toFixed(0)}%`)
                }
                if (
                  tooltipData.categoryPercentage !== undefined &&
                  tooltipData.categoryPercentage > 0
                ) {
                  percentageParts.push(`${tooltipData.categoryPercentage.toFixed(0)}%`)
                }
                const amount = formatCurrency(tooltipData.value)
                return percentageParts.length > 0
                  ? `${amount} (${percentageParts.join(", ")})`
                  : amount
              })()}
            </PrivacyBlur>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation()
              closeTooltip()
            }}
            className="absolute top-1 right-1 text-muted-foreground hover:text-foreground text-lg leading-none"
          >
            ×
          </button>
        </motion.div>
      )
    }

    // Recharts may call tooltip even when not active, but we should still check
    if (!active) {
      return null
    }

    if (!payload || payload.length === 0) {
      return null
    }

    const p = payload[0]
    // Recharts Sankey passes link data in payload.payload.payload
    // The source and target are full node objects with color, name, etc.
    const linkData = p.payload?.payload || p.payload || p

    // Check if we have the nested structure with source and target as objects
    if (linkData && linkData.source && linkData.target && typeof linkData.source === "object") {
      // Source and target are full node objects
      const sourceNode = linkData.source
      const targetNode = linkData.target
      const value = linkData.value || 0

      if (!sourceNode || !targetNode) {
        return null
      }

      // Get colors directly from the node objects provided by recharts
      const sourceColor = sourceNode.color || "#3b82f6"
      const targetColor = targetNode.color || "#94a3b8"

      // Calculate percentages
      const partnerPercentage = linkData.percentage || 0 // Percentage of partner's total spending
      const categoryPercentage =
        targetNode?.value && targetNode.value > 0 ? (value / targetNode.value) * 100 : 0 // Percentage of category's total income

      // On mobile, store tooltip data on hover/click for persistent display
      if (isMobile && coordinate) {
        // Use mouse position if available (more accurate), otherwise use coordinate
        const pos = mousePositionRef.current || {
          x: coordinate.x || 0,
          y: coordinate.y || 0,
        }
        // Store tooltip data in ref (will be applied in useEffect to avoid setState during render)
        pendingTooltipDataRef.current = {
          sourceNode,
          targetNode,
          value,
          partnerPercentage,
          categoryPercentage,
          x: pos.x,
          y: pos.y,
        }
        // Don't show hover tooltip on mobile
        return null
      }

      return (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.15 }}
          className="rounded-md border bg-popover p-2 text-sm shadow-md z-50"
        >
          <div className="flex items-center gap-2 font-medium">
            {/* Source color bar */}
            <div
              className="flex-shrink-0"
              style={{
                width: "4px",
                height: "20px",
                backgroundColor: sourceColor,
                borderRadius: "2px",
                display: "inline-block",
                flexShrink: 0,
                verticalAlign: "middle",
                minWidth: "4px",
                minHeight: "20px",
              }}
            />
            <span>{sourceNode.name}</span>
            <span className="text-muted-foreground">→</span>
            <span>{targetNode.name}</span>
            {/* Target color bar */}
            <div
              className="flex-shrink-0"
              style={{
                width: "4px",
                height: "20px",
                backgroundColor: targetColor,
                borderRadius: "2px",
                display: "inline-block",
                flexShrink: 0,
                verticalAlign: "middle",
                minWidth: "4px",
                minHeight: "20px",
              }}
            />
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            <PrivacyBlur>
              {(() => {
                const percentageParts: string[] = []
                if (partnerPercentage > 0) {
                  percentageParts.push(`${partnerPercentage.toFixed(0)}%`)
                }
                if (categoryPercentage > 0) {
                  percentageParts.push(`${categoryPercentage.toFixed(0)}%`)
                }
                const amount = formatCurrency(value)
                return percentageParts.length > 0
                  ? `${amount} (${percentageParts.join(", ")})`
                  : amount
              })()}
            </PrivacyBlur>
          </div>
        </motion.div>
      )
    }

    // Fallback: try to handle if source/target are indices (legacy support)
    if (linkData && typeof linkData.source === "number" && typeof linkData.target === "number") {
      const sourceIndex = linkData.source
      const targetIndex = linkData.target

      if (
        sourceIndex >= 0 &&
        sourceIndex < nodes.length &&
        targetIndex >= 0 &&
        targetIndex < nodes.length
      ) {
        const sourceNode = nodes[sourceIndex]
        const targetNode = nodes[targetIndex]
        const value = linkData.value || 0

        // Get colors from nodes
        const sourceColor = sourceNode.color || "#3b82f6"
        const targetColor = targetNode.color || "#94a3b8"

        // Calculate percentages
        const partnerPercentage = linkData.percentage || 0 // Percentage of partner's total spending
        const categoryPercentage =
          targetNode?.value && targetNode.value > 0 ? (value / targetNode.value) * 100 : 0 // Percentage of category's total income

        if (isMobile && coordinate) {
          // Use mouse position if available (more accurate), otherwise use coordinate
          const pos = mousePositionRef.current || {
            x: coordinate.x || 0,
            y: coordinate.y || 0,
          }
          // Store tooltip data in ref (will be applied in useEffect to avoid setState during render)
          pendingTooltipDataRef.current = {
            sourceNode,
            targetNode,
            value,
            partnerPercentage,
            categoryPercentage,
            x: pos.x,
            y: pos.y,
          }
          return null
        }

        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="rounded-md border bg-popover p-2 text-sm shadow-md z-50"
          >
            <div className="flex items-center gap-2 font-medium">
              {/* Source color bar */}
              <div
                className="flex-shrink-0"
                style={{
                  width: "4px",
                  height: "20px",
                  backgroundColor: sourceColor,
                  borderRadius: "2px",
                  display: "inline-block",
                  flexShrink: 0,
                  verticalAlign: "middle",
                  minWidth: "4px",
                  minHeight: "20px",
                }}
              />
              <span>{sourceNode.name}</span>
              <span className="text-muted-foreground">→</span>
              <span>{targetNode.name}</span>
              {/* Target color bar */}
              <div
                className="flex-shrink-0"
                style={{
                  width: "4px",
                  height: "20px",
                  backgroundColor: targetColor,
                  borderRadius: "2px",
                  display: "inline-block",
                  flexShrink: 0,
                  verticalAlign: "middle",
                  minWidth: "4px",
                  minHeight: "20px",
                }}
              />
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              <PrivacyBlur>
                {(() => {
                  const percentageParts: string[] = []
                  if (partnerPercentage > 0) {
                    percentageParts.push(`${partnerPercentage.toFixed(0)}%`)
                  }
                  if (categoryPercentage > 0) {
                    percentageParts.push(`${categoryPercentage.toFixed(0)}%`)
                  }
                  const amount = formatCurrency(value)
                  return percentageParts.length > 0
                    ? `${amount} (${percentageParts.join(", ")})`
                    : amount
                })()}
              </PrivacyBlur>
            </div>
          </motion.div>
        )
      }
    }

    // For nodes, payload contains node data
    const nodeData = p.payload as RechartsSankeyNode
    if (nodeData?.name) {
      return (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.15 }}
          className="rounded-md border bg-popover p-3 text-sm shadow-md z-50"
        >
          <div className="flex items-center gap-2 font-medium">
            {/* Node color bar - slim */}
            <div
              className="flex-shrink-0"
              style={{
                width: "2px",
                height: "16px",
                backgroundColor: nodeData.color || "#94a3b8",
                borderRadius: "1px",
              }}
            />
            <span>{nodeData.name}</span>
          </div>
          {nodeData.value && (
            <div className="text-xs text-muted-foreground mt-1">
              Total: {nodeData.value.toFixed(2)} €
            </div>
          )}
        </motion.div>
      )
    }

    return null
  }

  // Mobile tooltip overlay component
  const MobileTooltipOverlay = () => {
    if (!isMobile || !tooltipData) return null

    return (
      <>
        <CustomTooltip active={true} payload={[]} />
        {/* Click outside to close mobile tooltip */}
        <div className="fixed inset-0 z-40" onClick={closeTooltip} />
      </>
    )
  }

  return {
    CustomTooltip,
    MobileTooltipOverlay,
    closeTooltip,
    tooltipData,
  }
}
